import * as v from 'valibot';

import { vcGcid, vcOptionalWithNullToUndefined, vcUcid } from '../../../../utils/valibot/custom-types';
import { newSchema } from '../../../../utils/valibot/schema';

export enum GcdmPartnerCategory {
  PERSON = 'PERSON',
  ORGANISATION = 'ORGANISATION',
}

/** The category of an address or communication entry. */
export enum GcdmCommunicationStatus {
  PRIVATE = 'PRIVATE',
  BUSINESS = 'BUSINESS',
}

/** Alias for addresses, which use the same PRIVATE / BUSINESS values. */
export const GcdmAddressStatus = GcdmCommunicationStatus;
export type GcdmAddressStatus = GcdmCommunicationStatus;

export enum GcdmPhoneCommunicationType {
  TEL = 'TEL',
  MOB = 'MOB',
  FAX = 'FAX',
}

export enum GcdmEmailCommunicationType {
  EMAIL = 'EMAIL',
  /** Electronic post, not an email address. */
  EPOST = 'EPOST',
}

export enum GcdmCustomerStatus {
  ACTIVE = 'ACTIVE',
  IN_ACQUISITION = 'IN_ACQUISITION',
  INACTIVE = 'INACTIVE',
  DEAD = 'DEAD',
}

/** GCDM's VAT type, Retail CRM's `vatGroup`. */
export enum GcdmVatType {
  STANDARD = 'standard',
  FREE = 'free',
  REDUCED = 'reduced',
}

export const GcdmAddressSchema = newSchema(
  'GcdmAddressSchema',
  v.object({
    street: vcOptionalWithNullToUndefined(v.string()),
    houseNumber: vcOptionalWithNullToUndefined(v.string()),
    postalCode: vcOptionalWithNullToUndefined(v.string()),
    city: vcOptionalWithNullToUndefined(v.string()),
    country: vcOptionalWithNullToUndefined(v.string()),
    regionCode: vcOptionalWithNullToUndefined(v.string()),
    /** Address supplement 1 (CDF: addressLine1). */
    strSuppl1: vcOptionalWithNullToUndefined(v.string()),
    /** Address supplement 2 (CDF: addressLine2). */
    strSuppl2: vcOptionalWithNullToUndefined(v.string()),
    postOfficeBox: vcOptionalWithNullToUndefined(v.string()),
    /** Required by GCDM: the category of this address. */
    addressStatus: vcOptionalWithNullToUndefined(v.enum(GcdmAddressStatus)),
    /** Preferred within its category; at most one per category. */
    preferredFor: vcOptionalWithNullToUndefined(v.enum(GcdmAddressStatus)),
    /** The single globally preferred address. */
    preferred: vcOptionalWithNullToUndefined(v.boolean()),
  }),
);

export const GcdmAddressesSchema = newSchema(
  'GcdmAddressesSchema',
  v.object({
    addresses: vcOptionalWithNullToUndefined(v.array(GcdmAddressSchema)),
  }),
);

export const GcdmCommunicationEmailSchema = newSchema(
  'GcdmCommunicationEmailSchema',
  v.object({
    value: vcOptionalWithNullToUndefined(v.string()),
    preferred: vcOptionalWithNullToUndefined(v.boolean()),
    /** Required by GCDM: EMAIL or EPOST. */
    communicationType: vcOptionalWithNullToUndefined(v.enum(GcdmEmailCommunicationType)),
    communicationStatus: vcOptionalWithNullToUndefined(v.enum(GcdmCommunicationStatus)),
    /** Resolves duplicates within a type/status pair; see GcdmExtendedCustomerViewModelMapper. */
    updatedAt: vcOptionalWithNullToUndefined(v.string()),
  }),
);

export const GcdmCommunicationPhoneSchema = newSchema(
  'GcdmCommunicationPhoneSchema',
  v.object({
    value: vcOptionalWithNullToUndefined(v.string()),
    preferred: vcOptionalWithNullToUndefined(v.boolean()),
    communicationType: vcOptionalWithNullToUndefined(v.enum(GcdmPhoneCommunicationType)),
    communicationStatus: vcOptionalWithNullToUndefined(v.enum(GcdmCommunicationStatus)),
    /** Resolves duplicates within a type/status pair; see GcdmExtendedCustomerViewModelMapper. */
    updatedAt: vcOptionalWithNullToUndefined(v.string()),
  }),
);

export const GcdmCommunicationsSchema = newSchema(
  'GcdmCommunicationsSchema',
  v.object({
    communicationEMails: vcOptionalWithNullToUndefined(v.array(GcdmCommunicationEmailSchema)),
    communicationPhones: vcOptionalWithNullToUndefined(v.array(GcdmCommunicationPhoneSchema)),
  }),
);

export const GcdmTaxDataSchema = newSchema(
  'GcdmTaxDataSchema',
  v.object({
    taxId: vcOptionalWithNullToUndefined(v.string()),
    vatRegNo: vcOptionalWithNullToUndefined(v.string()),
    taxCountryISO: vcOptionalWithNullToUndefined(v.string()),
    vatType: vcOptionalWithNullToUndefined(v.enum(GcdmVatType)),
  }),
);

export const GcdmBusinessDataSchema = newSchema(
  'GcdmBusinessDataSchema',
  v.object({
    insolvent: vcOptionalWithNullToUndefined(v.boolean()),
  }),
);

export const GcdmPersonalIdentificationSchema = newSchema(
  'GcdmPersonalIdentificationSchema',
  v.object({
    id: vcOptionalWithNullToUndefined(v.string()),
    /** PER_IDE_PASSPORT, PER_IDE_IDENTITY_CARD, ... */
    type: vcOptionalWithNullToUndefined(v.string()),
  }),
);

export const GcdmProfileSchema = newSchema(
  'GcdmProfileSchema',
  v.object({
    personalIdentifications: vcOptionalWithNullToUndefined(
      v.object({
        personalIdentifications: vcOptionalWithNullToUndefined(v.array(GcdmPersonalIdentificationSchema)),
      }),
    ),
  }),
);

/**
 * A GCDM business partner, i.e. a customer (person or organisation).
 *
 * Note that GCDM's "business partner" is unrelated to CDF's `businessPartnerId`,
 * which identifies the dealer of the logged-in user.
 */
export const GcdmBusinessPartnerSchema = newSchema(
  'GcdmBusinessPartnerSchema',
  v.object({
    gcid: vcOptionalWithNullToUndefined(vcGcid),
    ucid: vcOptionalWithNullToUndefined(vcUcid),
    partnerCategory: vcOptionalWithNullToUndefined(v.enum(GcdmPartnerCategory)),
    givenName: vcOptionalWithNullToUndefined(v.string()),
    middleName: vcOptionalWithNullToUndefined(v.string()),
    surname: vcOptionalWithNullToUndefined(v.string()),
    additionalFamilyName: vcOptionalWithNullToUndefined(v.string()),
    /** JR / SR */
    nameSuffixGeneration: vcOptionalWithNullToUndefined(v.string()),
    /** The email address of the customer's BMW/MINI ID, if they have one. */
    customerStatus: vcOptionalWithNullToUndefined(v.enum(GcdmCustomerStatus)),
    initialsName: vcOptionalWithNullToUndefined(v.string()),
    /** yyyy-MM-dd */
    birthday: vcOptionalWithNullToUndefined(v.string()),
    /** SAL_MR, SAL_MS, ... */
    salutation: vcOptionalWithNullToUndefined(v.string()),
    /** TIT_DR, TIT_PROF, TIT_PROF_DR */
    title: vcOptionalWithNullToUndefined(v.string()),
    nationalityISO: vcOptionalWithNullToUndefined(v.string()),
    /** Spoken / preferred language - not modelled by CDF, do not map to correspondenceLanguage. */
    correspondLanguageISO: vcOptionalWithNullToUndefined(v.string()),
    /** Correspondence language (Retail CRM Language__pc). */
    documentLanguage: vcOptionalWithNullToUndefined(v.string()),
    commerciallySelfEmployed: vcOptionalWithNullToUndefined(v.boolean()),
    /** Only `city` is read: it is Retail CRM's place of birth. */
    birthAddress: vcOptionalWithNullToUndefined(GcdmAddressSchema),
    businessData: vcOptionalWithNullToUndefined(GcdmBusinessDataSchema),
    profile: vcOptionalWithNullToUndefined(GcdmProfileSchema),
    loginIdMail: vcOptionalWithNullToUndefined(v.string()),
    addresses: vcOptionalWithNullToUndefined(GcdmAddressesSchema),
    communications: vcOptionalWithNullToUndefined(GcdmCommunicationsSchema),
    taxDatas: vcOptionalWithNullToUndefined(v.array(GcdmTaxDataSchema)),
  }),
);

/** `GET gcdm/customers/{gcid}` answers with a single business partner, not a list. */
export const GcdmSingleBusinessPartnerSchema = GcdmBusinessPartnerSchema;

export const GcdmBusinessPartnerResponseSchema = newSchema(
  'GcdmBusinessPartnerResponseSchema',
  v.array(GcdmBusinessPartnerSchema),
);

export type GcdmAddress = v.InferOutput<typeof GcdmAddressSchema>;
export type GcdmCommunicationEmail = v.InferOutput<typeof GcdmCommunicationEmailSchema>;
export type GcdmCommunicationPhone = v.InferOutput<typeof GcdmCommunicationPhoneSchema>;
export type GcdmTaxData = v.InferOutput<typeof GcdmTaxDataSchema>;
export type GcdmBusinessPartner = v.InferOutput<typeof GcdmBusinessPartnerSchema>;
export type GcdmBusinessPartnerResponse = v.InferOutput<typeof GcdmBusinessPartnerResponseSchema>;
export type GcdmPersonalIdentification = v.InferOutput<typeof GcdmPersonalIdentificationSchema>;
export type GcdmProfile = v.InferOutput<typeof GcdmProfileSchema>;
export type GcdmBusinessData = v.InferOutput<typeof GcdmBusinessDataSchema>;
